import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Container,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';

import {
  ContentContainer,
  getChoiceButtonStyle,
} from './AccessCodeSpark.styles';

import sparkTheme from '@/themes/sparkTheme';
import { SelectButton } from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { useAdoptPostHogIdentity } from '@/hooks/useAdoptPostHogIdentity';

const AccessCodeSparkInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const choiceButtonStyle = getChoiceButtonStyle(theme);
  const location = useLocation<{ error?: string }>();
  const { search } = location;
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const accessCode = params.get('accessCode') ?? undefined;

  // A prefilled access code (a Xealth order's deep link) redirects to
  // AccessCodeEntry on mount, so nobody actually sees this screen. It therefore
  // does no identity work and captures no landing events on that path —
  // AccessCodeEntry owns both, and decodes the forwarded token itself. Decoding
  // here as well would spend a second request on a result this screen unmounts
  // before it can use.
  const willRedirect = Boolean(accessCode);

  const { ready: identityReady, loading: identityLoading } = useAdoptPostHogIdentity({
    enabled: !willRedirect,
    onReady: () => {
      posthog?.capture('SparkAccessCodeScreenViewed');
      posthog?.capture('SignUpFlowLanded');
    },
  });

  // Both ways into AccessCodeEntry — this screen's own redirect and the Yes button
  // — carry the partner-identity param through unchanged (AccessCodeEntry decodes
  // ph_token itself). Dropping it on either would leave AccessCodeEntry with no
  // identity to adopt, and it would reset instead.
  const entryPath = useMemo(() => {
    const forwardedParams = new URLSearchParams();
    const phDistinctIdParam = params.get('ph_distinct_id');
    const phTokenParam = params.get('ph_token');
    if (phDistinctIdParam) {
      forwardedParams.set('ph_distinct_id', phDistinctIdParam);
    } else if (phTokenParam) {
      forwardedParams.set('ph_token', phTokenParam);
    }
    const forwardedQuery = forwardedParams.toString();
    return `/activate/${product}/access-code/entry${forwardedQuery ? `?${forwardedQuery}` : ''}`;
  }, [params, product]);

  useEffect(() => {
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }

    if (accessCode) {
      // No fromFlowEntry marker: this screen skipped its identity work, so
      // AccessCodeEntry still has to reset or identify.
      history.push(entryPath, { accessCode });
    }
  }, [params, history, accessCode, entryPath]);

  // Both handlers register a flow property and capture, so neither may run before
  // the identity decision — fired first they would land on whoever the browser was
  // identified as last, the same way the landing events did. The buttons are
  // disabled only while a partner token is actually decoding, which is also how
  // LoginPhone gates its email button; with no token in the URL the decision is
  // immediate and nothing is ever disabled.
  const handleYesClick = () => {
    posthog?.register({ flow: 'employer' });
    posthog?.capture('SignUpFlowClicked');
    // fromFlowEntry tells AccessCodeEntry that identity was already settled for
    // this activation, so it skips its own reset(). Without it, that reset clears
    // the flow property registered on the line above and starts a second session
    // one screen into the activation — the trap PreOnboardingPage avoids with its
    // own fromEmployerPath marker (PAT-1220). It is only true once the decision
    // has actually been applied: clicked mid-decode, AccessCodeEntry must still
    // make it.
    history.push(entryPath, { accessCode, fromFlowEntry: identityReady });
  };

  const handleNoClick = () => {
    posthog?.register({ flow: 'order' });
    posthog?.capture('SignUpFlowClicked');
    history.push(`/activate/${product}/employers`);
  };

  return (
    <ContentContainer>
      <Header />

      <Container maxWidth="xs" style={{ flex: 1 }}>
        {location.state?.error === 'email_verification_failed' && (
          <Typography color="error" style={{ marginTop: 16 }} data-testid="email-verification-error">
            {t('coverage.pre_onboarding.access_code.email_verification_error')}
          </Typography>
        )}
        {location.state?.error === 'activation_failed' && (
          <Typography color="error" style={{ marginTop: 16 }} data-testid="activation-error">
            {t('coverage.errors.generic_error')}
          </Typography>
        )}
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.pre_onboarding.access_code.title')}
          </Typography>
          <Typography color="textPrimary">
            <Trans
              i18nKey="coverage.pre_onboarding.access_code.sub_title"
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Box>

        <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleYesClick}
            disabled={identityLoading}
            data-testid="access-code-yes-button"
          >
            {t('coverage.pre_onboarding.access_code.yes')}
          </SelectButton>

          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleNoClick}
            disabled={identityLoading}
            data-testid="access-code-no-button"
          >
            {t('coverage.pre_onboarding.access_code.no')}
          </SelectButton>
        </Box>
        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const AccessCodeSpark: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <AccessCodeSparkInner />
  </ThemeProvider>
);

export default AccessCodeSpark;
